'use client';

import { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';
import type { ClientAppConfig } from '@/lib/env';
import { LicenseManager } from 'ag-grid-enterprise';
import { setApiConfig } from '@/lib/api/client';
import { setDataConduitBaseUrl } from '@/lib/api/dataConduit';

const DEFAULT_CONFIG: ClientAppConfig = {
  app: {
    name: 'AssetCAST',
    basePath: '/assetcast',
  },
  api: {
    baseUrl: 'http://localhost:8090',
  },
  dataConduit: {
    baseUrl: 'http://localhost:3001',
  },
  maps: {
    mapboxToken: '',
    esriApiKey: '',
  },
  features: {
    agGridLicenseKey: '',
  },
};

const ClientConfigContext = createContext<ClientAppConfig>(DEFAULT_CONFIG);

export function ClientConfigProvider({
  config,
  children,
}: {
  config: ClientAppConfig;
  children: ReactNode;
}) {
  useEffect(() => {
    if (config.features.agGridLicenseKey) {
      LicenseManager.setLicenseKey(config.features.agGridLicenseKey);
    }

    setApiConfig({ baseUrl: config.api.baseUrl });
    setDataConduitBaseUrl(config.dataConduit.baseUrl);
  }, [
    config.features.agGridLicenseKey,
    config.api.baseUrl,
    config.dataConduit.baseUrl,
    config.maps.mapboxToken,
    config.maps.esriApiKey,
  ]);

  return (
    <ClientConfigContext.Provider value={config}>
      {children}
    </ClientConfigContext.Provider>
  );
}

export function useClientConfig(): ClientAppConfig {
  return useContext(ClientConfigContext);
}

export function useConfigSection<K extends keyof ClientAppConfig>(
  section: K
): ClientAppConfig[K] {
  const config = useClientConfig();
  return useMemo(() => config[section], [config, section]);
}
